import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import {
    FiltersButton,
    ReactSelect,
    TableHeader,
    WithModulesAccess,
    DropdownServices,
    WithRoleAccess,
} from "@services/ui-components";
import VioletBadge from "@services/ui-components/src/VioletBadge/VioletBadge";
import ServiceProvidersTable from "./ServiceProvidersTable/ServiceProvidersTable";
import { useReactiveVar } from "@apollo/client";
import { useGetEmployeesPositionsQuery } from "@me-team/host/main/addEmployee/graphql/addEmployee.hooks";
import { currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { AllModulesTagName } from "../../utils/enums";
import { CompanyAccess } from "@me-team/host/main/graphql/types";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import { useGetBranchNameQuery } from "@me-team/host/main/branches/graphql/branches.hooks";
import { Option } from "../../interfaces/interfaces";

export interface Filter {
    name?: string;
    positionId?: number;
    branchId?: number;
}

const ServiceProviders: React.FC = () => {
    const { t } = useTranslation();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const canManageCompanyEmployees = useRoleAccess(CompanyAccess.AccessManageCompanyEmployees);
    const canViewServiceTechMaps = useRoleAccess(CompanyAccess.ServiceTechMapsView);
    const { id, branchId } = useParams();
    const inputRef = useRef(null);
    const [showFilters, setShowFilters] = useState(false);
    const [filter, setFilter] = useState<Filter>({
        name: null,
        positionId: null,
        branchId: Number(branchId),
    });
    const [currentFilter, setCurrentFilter] = useState({ name: inputRef.current?.value, positionId: null });
    const [name, setName] = useState();
    const { data: branchNameData, loading: branchNameLoading } = useGetBranchNameQuery({
        variables: { companyId: Number(currentCompanyId), branchId: Number(branchId) },
        skip: !currentCompanyId || !branchId,
        onError: (error) => {
            console.error(error);
        },
    });
    const [selectedBranch, setSelectedBranch] = useState<Option>(null);

    const currentBranchName: string = branchNameData?.user?.company?.branchesPaginated?.branches[0]?.name;
    const currentBranchOption: Option = { value: Number(branchId), label: currentBranchName };

    const normalizeQuery = (s?: string) => (s ?? "").replace(/\s+/g, " ").trim();

    useEffect(() => {
        if (!currentBranchName) return;

        setSelectedBranch({
            value: Number(branchId),
            label: currentBranchName,
        });
    }, [branchId, currentBranchName]);

    const branchOptions = [currentBranchOption, { value: -1, label: t("All branches") }];

    const { data: possitions } = useGetEmployeesPositionsQuery({
        variables: { companyId: +currentCompanyId },
        skip: !currentCompanyId,
        onError: (error) => {
            console.error(error);
        },
    });
    const toggleFilters = () => setShowFilters(!showFilters);

    const handlePositionChange = (selectedOption: any) => {
        setCurrentFilter((prevFilter) => ({
            ...prevFilter,
            positionId: selectedOption?.value,
        }));
    };

    const applyFilters = () => {
        const raw = inputRef.current?.value ?? "";
        const name = normalizeQuery(raw);
        if (inputRef.current) inputRef.current.value = name;

        setFilter({
            name: name,
            positionId: currentFilter.positionId,
            branchId: selectedBranch?.value && selectedBranch?.value,
        });
    };
    const clearFilters = () => {
        if (inputRef.current) {
            inputRef.current.value = "";
        }
        setCurrentFilter({ name: null, positionId: null });
        setFilter({ name: null, positionId: null, branchId: Number(branchId) });
        setSelectedBranch(currentBranchOption);
    };
    const positionsOptions = possitions?.user?.company?.employeePositions?.employeePositions.map((position) => ({
        value: position.id,
        label: position.name,
    }));
    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            applyFilters();
        }
    };

    if (branchNameLoading) return <Spinner />;

    return (
        <>
            <VioletBadge
                text={t(
                    "On this page, you can choose service providers and specify different service durations and prices for different employees."
                )}
            />
            <TableHeader
                title={`${t("Service Providers")} «${name || " "}»`}
                paths={[
                    { path: `/services-providers/${id}/${branchId}`, label: t("Services") },
                    {
                        path: `/services/service/${currentCompanyId}/service-list/${branchId}`,
                        label: t("Service list"),
                    },
                    { path: `/services-providers/${id}/${branchId}`, label: t("Service Providers") },
                ]}
                buttons={
                    <Row className="mt-4 mt-lg-0 gx-3 justify-content-end">
                        <WithModulesAccess modules={[AllModulesTagName.WAREHOUSES]}>
                            <WithRoleAccess permission={CompanyAccess.ServiceTechMapsView}>
                                <Col xs sm={4} lg={6} xxl className="mb-3 mb-sm-0">
                                    <Link
                                        to={`/services/service/${currentCompanyId}/service-list/${id}/technological/maps/branch/${branchId}`}
                                        className="btn btn-orange px-1 w-100 text-truncate text-capitalize fw-bold"
                                    >
                                        {t("To Tech cards")}
                                    </Link>
                                </Col>
                            </WithRoleAccess>
                        </WithModulesAccess>
                        <Col
                            xs={12}
                            sm={canViewServiceTechMaps ? 4 : 6}
                            lg={6}
                            xxl={canViewServiceTechMaps ? 4 : 5}
                            className={`mb-3 ${canViewServiceTechMaps ? "mb-xxl-0" : "mb-sm-0"}`}
                        >
                            <DropdownServices currentCompanyId={currentCompanyId} serviceId={id} branchId={branchId} />
                        </Col>
                        <Col sm={canViewServiceTechMaps ? 4 : 6} lg={6} xxl={3}>
                            <FiltersButton filter={filter} showFilters={showFilters} onClick={toggleFilters} />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
                colSize={7}
                btnColSize={5}
            >
                <Row className="gx-3 mb-3">
                    <Col lg={8} xl={9}>
                        <Row className="gx-3">
                            <Col lg={6} className="text-primary">
                                <div className="position-relative mb-3 mb-lg-0">
                                    <input
                                        type="text"
                                        name="name"
                                        id="serviceProviders-search-field"
                                        className="form-control ps-4 pe-5 py-6 text-truncate"
                                        placeholder={t("Search by name, surname, phone or e-mail")}
                                        ref={inputRef}
                                        onKeyDown={handleSearchKeyDown}
                                    />
                                    <i className="bi bi-search position-absolute top-50 end-0 translate-middle-y text-dark me-2"></i>
                                </div>
                            </Col>
                            <Col lg={canManageCompanyEmployees ? 3 : 6} className="text-primary  mb-3 mb-lg-0">
                                <ReactSelect
                                    name={"serviceProviders-selectPosition-field"}
                                    id={"serviceProviders-selectPosition-field"}
                                    options={positionsOptions}
                                    placeholder={t("Select Position")}
                                    onChange={handlePositionChange}
                                    value={
                                        currentFilter.positionId
                                            ? positionsOptions.find(
                                                  (option) => option.value === currentFilter.positionId
                                              )
                                            : null
                                    }
                                    isSearchable={true}
                                />
                            </Col>
                            {canManageCompanyEmployees ? (
                                <Col lg={3} className="mb-3 mb-xl-0">
                                    <ReactSelect
                                        id="serviceProviders-branchId-field"
                                        placeholder={t("Branch")}
                                        options={branchOptions}
                                        value={branchOptions.find((option) => option.value === selectedBranch?.value)}
                                        onChange={(selectedOption: Option) => setSelectedBranch(selectedOption)}
                                        isSearchable={false}
                                    />
                                </Col>
                            ) : null}
                        </Row>
                    </Col>
                    <Col lg={4} xl={3}>
                        <Row className="gx-3 h-100">
                            <Col xs={6} className="mb-3 mb-lg-0">
                                <button
                                    type="button"
                                    className="btn btn-outline-dark border-grey w-100 fw-normal"
                                    onClick={clearFilters}
                                >
                                    {t("clear")}
                                </button>
                            </Col>
                            <Col xs={6} className="mb-3 mb-lg-0">
                                <button className="btn btn-primary w-100 px-1" onClick={applyFilters}>
                                    {t("apply")}
                                </button>
                            </Col>
                        </Row>
                    </Col>
                </Row>
            </TableHeader>

            <ServiceProvidersTable filter={filter} setServiceName={setName} />
        </>
    );
};

export default ServiceProviders;
